import ExcelJS from "exceljs";
import { BRAND, NEUTRAL } from "../components/corebase/theme/brand";

const NAVY = argb(BRAND.navy);
const SKY = argb(BRAND.sky);
const BRIGHT = argb(BRAND.bright);
const LIGHT = argb(BRAND.light);
const WHITE = argb(BRAND.white);
const GRAY = argb("#F4F7FA");
const TEXT = argb(NEUTRAL.text);
const MUTED = argb(NEUTRAL.textSecondary);
const BORDER = argb(NEUTRAL.border);

const FONT = "Calibri";
const PERCENT = "0.00%";
const INTEGER = "0";
const PLAN_HEADERS = [
  "Mes",
  "Saldo inicial",
  "Interés",
  "Pago a capital",
  "Amort. extra",
  "Pago total",
  "Saldo final",
];

export function moneyFormat(currency) {
  const symbol = String(currency || "").trim().replace(/"/g, "");
  return symbol ? `"${symbol} "#,##0.00` : "#,##0.00";
}

function argb(hex) {
  return `FF${String(hex || "").replace("#", "").toUpperCase()}`;
}

function stamp(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}`;
}

function fechaLegible(date = new Date()) {
  return date.toLocaleString("es-BO", { dateStyle: "short", timeStyle: "short" });
}

const hairline = { style: "thin", color: { argb: BORDER } };
const gridBorder = { top: hairline, left: hairline, bottom: hairline, right: hairline };

function solid(argbColor) {
  return { type: "pattern", pattern: "solid", fgColor: { argb: argbColor } };
}

function paintRow(row, lastCol, fillArgb) {
  for (let c = 1; c <= lastCol; c += 1) {
    row.getCell(c).fill = solid(fillArgb);
  }
}

function formula(expression, result) {
  return { formula: expression, result };
}

function setupPage(sheet, orientation) {
  sheet.pageSetup.margins = { left: 0.5, right: 0.5, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 };
  sheet.pageSetup.orientation = orientation;
  sheet.pageSetup.horizontalCentered = true;
  sheet.headerFooter.oddFooter = "&L&8DevsSoft · www.devssoft.com&R&8Página &P de &N";
}

function applyBrandBanner(sheet, lastCol, subtitle) {
  sheet.mergeCells(1, 1, 1, lastCol);
  const brand = sheet.getCell(1, 1);
  brand.value = "DEVSSOFT";
  brand.font = { name: FONT, bold: true, size: 22, color: { argb: WHITE } };
  brand.alignment = { vertical: "bottom", horizontal: "left", indent: 1 };
  sheet.getRow(1).height = 38;
  paintRow(sheet.getRow(1), lastCol, NAVY);

  sheet.mergeCells(2, 1, 2, lastCol);
  const title = sheet.getCell(2, 1);
  title.value = subtitle;
  title.font = { name: FONT, size: 13, color: { argb: LIGHT } };
  title.alignment = { vertical: "top", horizontal: "left", indent: 1 };
  sheet.getRow(2).height = 24;
  paintRow(sheet.getRow(2), lastCol, NAVY);

  sheet.mergeCells(3, 1, 3, lastCol);
  const meta = sheet.getCell(3, 1);
  meta.value = `Generado el ${fechaLegible()}`;
  meta.font = { name: FONT, size: 9, italic: true, color: { argb: MUTED } };
  meta.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
  sheet.getRow(3).height = 20;
  paintRow(sheet.getRow(3), lastCol, GRAY);
  for (let c = 1; c <= lastCol; c += 1) {
    sheet.getCell(3, c).border = { bottom: { style: "medium", color: { argb: BRIGHT } } };
  }
  sheet.getRow(4).height = 10;
}

function sectionLabel(sheet, rowNumber, lastCol, text, fillArgb) {
  sheet.mergeCells(rowNumber, 1, rowNumber, lastCol);
  const cell = sheet.getCell(rowNumber, 1);
  cell.value = text.toUpperCase();
  cell.font = { name: FONT, bold: true, size: 10, color: { argb: WHITE } };
  cell.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
  sheet.getRow(rowNumber).height = 22;
  paintRow(sheet.getRow(rowNumber), lastCol, fillArgb);
}

function kvRow(sheet, rowNumber, label, value, { numFmt, zebra = false, accent = false, emphasis = false } = {}) {
  const row = sheet.getRow(rowNumber);
  row.height = emphasis ? 26 : 21;
  const fill = zebra ? GRAY : WHITE;
  const bottom = emphasis ? { style: "medium", color: { argb: BRIGHT } } : hairline;

  const labelCell = row.getCell(1);
  labelCell.value = label;
  labelCell.font = { name: FONT, size: 11, bold: emphasis, color: { argb: emphasis ? TEXT : MUTED } };
  labelCell.alignment = { vertical: "middle", horizontal: "left", indent: 1 };

  const valueCell = row.getCell(2);
  valueCell.value = value;
  valueCell.font = {
    name: FONT,
    bold: true,
    size: emphasis ? 14 : accent ? 12 : 11,
    color: { argb: accent ? BRIGHT : TEXT },
  };
  valueCell.alignment = { vertical: "middle", horizontal: numFmt ? "right" : "left", indent: 1 };
  if (numFmt) valueCell.numFmt = numFmt;

  [labelCell, valueCell].forEach((cell) => {
    cell.fill = solid(fill);
    cell.border = { bottom };
  });
}

function footerNote(sheet, rowNumber, lastCol, text) {
  sheet.mergeCells(rowNumber, 1, rowNumber, lastCol);
  const note = sheet.getCell(rowNumber, 1);
  note.value = text;
  note.font = { name: FONT, size: 9, italic: true, color: { argb: MUTED } };
  note.alignment = { wrapText: true, vertical: "middle", horizontal: "left", indent: 1 };
  note.fill = solid(GRAY);
  note.border = { left: { style: "thick", color: { argb: SKY } } };
  sheet.getRow(rowNumber).height = 46;
}

export function buildLoanExportModel({
  loanAmount,
  annualRate,
  numPeriods,
  amortizations,
  monthlyPayment,
  totalInterest,
  totalPaid,
  currency,
  paymentPlan = [],
}) {
  const rows = paymentPlan.map((row) => ([
    row.month,
    round2(row.initialBalance),
    round2(row.interest),
    round2(row.principalPayment),
    round2(row.extraAmortization),
    round2(row.totalPayment),
    round2(row.finalBalance),
  ]));
  const totals = paymentPlan.reduce((acc, row) => ({
    interest: acc.interest + (Number(row.interest) || 0),
    principal: acc.principal + (Number(row.principalPayment) || 0),
    extra: acc.extra + (Number(row.extraAmortization) || 0),
    paid: acc.paid + (Number(row.totalPayment) || 0),
  }), { interest: 0, principal: 0, extra: 0, paid: 0 });

  return {
    loanAmount: Number(loanAmount) || 0,
    annualRate: Number(annualRate) || 0,
    numPeriods: Number(numPeriods) || 0,
    mesesCalculados: paymentPlan.length,
    amortizations: String(amortizations || "").trim() || "—",
    monthlyPayment: round2(monthlyPayment),
    totalInterest: round2(totalInterest),
    totalPaid: round2(totalPaid),
    currency: String(currency || "").trim(),
    headers: PLAN_HEADERS,
    rows,
    totals: {
      interest: round2(totals.interest),
      principal: round2(totals.principal),
      extra: round2(totals.extra),
      paid: round2(totals.paid),
    },
  };
}

function round2(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export async function buildLoanWorkbook(model) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "DevsSoft";
  wb.lastModifiedBy = "DevsSoft";
  wb.created = new Date();
  wb.company = "DevsSoft";

  buildResumenSheet(wb, model);
  buildPlanSheet(wb, model);
  return wb;
}

const RESUMEN = "'Resumen'";
const PLAN = "'Plan de pagos'";
const CELL = {
  amount: "$B$6",
  rate: "$B$7",
  term: "$B$8",
  payment: "$B$13",
};
const PLAN_HEADER_ROW = 6;
const PLAN_FIRST_ROW = 7;

function buildResumenSheet(wb, model) {
  const sheet = wb.addWorksheet("Resumen", {
    views: [{ showGridLines: false }],
    pageSetup: { paperSize: 9, orientation: "portrait", fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
  });
  setupPage(sheet, "portrait");
  sheet.columns = [{ width: 42 }, { width: 26 }];
  applyBrandBanner(sheet, 2, "Calculadora de préstamos");
  const MONEY = moneyFormat(model.currency);

  sectionLabel(sheet, 5, 2, "Condiciones del crédito", SKY);
  kvRow(sheet, 6, "Monto del préstamo", model.loanAmount, { numFmt: MONEY });
  kvRow(sheet, 7, "Tasa de interés anual", model.annualRate / 100, { numFmt: PERCENT, zebra: true });
  kvRow(sheet, 8, "Plazo contratado (meses)", model.numPeriods, { numFmt: INTEGER });
  kvRow(sheet, 9, "Meses en el plan", model.mesesCalculados, { numFmt: INTEGER, zebra: true });
  kvRow(sheet, 10, "Amortizaciones extra", model.amortizations);

  const lastPlanRow = PLAN_FIRST_ROW + model.rows.length - 1;
  const interestFormula = model.rows.length
    ? formula(`SUM(${PLAN}!C${PLAN_FIRST_ROW}:C${lastPlanRow})`, model.totalInterest)
    : model.totalInterest;

  sheet.getRow(11).height = 12;
  sectionLabel(sheet, 12, 2, "Resultados", NAVY);
  kvRow(sheet, 13, "Cuota mensual base", formula(`PMT(B7/12,B8,-B6)`, model.monthlyPayment), { numFmt: MONEY, accent: true });
  kvRow(sheet, 14, "Total de intereses", interestFormula, { numFmt: MONEY, accent: true, zebra: true });
  kvRow(sheet, 15, "Total pagado (capital + intereses)", formula("B6+B14", model.totalPaid), { numFmt: MONEY, accent: true, emphasis: true });

  sheet.getRow(16).height = 12;
  footerNote(
    sheet,
    17,
    2,
    "Nota: la cuota mensual base se calcula con la fórmula PMT (PAGO). En los meses con amortización extraordinaria el pago total aumenta y el saldo baja más rápido, por lo que el plan puede terminar antes del plazo contratado. Documento generado por DevsSoft.",
  );
}

function buildPlanSheet(wb, model) {
  const sheet = wb.addWorksheet("Plan de pagos", {
    views: [{ state: "frozen", ySplit: PLAN_HEADER_ROW, showGridLines: false }],
    pageSetup: { paperSize: 9, orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  setupPage(sheet, "landscape");
  sheet.pageSetup.printTitlesRow = `${PLAN_HEADER_ROW}:${PLAN_HEADER_ROW}`;
  sheet.columns = [
    { width: 8 }, { width: 18 }, { width: 16 }, { width: 18 },
    { width: 17 }, { width: 18 }, { width: 18 },
  ];
  applyBrandBanner(sheet, 7, "Plan de amortización");
  const MONEY = moneyFormat(model.currency);
  sectionLabel(sheet, 5, 7, "Detalle mes a mes", SKY);

  const headerRow = sheet.getRow(PLAN_HEADER_ROW);
  headerRow.height = 30;
  model.headers.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { name: FONT, bold: true, size: 10, color: { argb: WHITE } };
    cell.fill = solid(NAVY);
    cell.alignment = { vertical: "middle", horizontal: i === 0 ? "center" : "right", wrapText: true, indent: i === 0 ? 0 : 1 };
    cell.border = { ...gridBorder, bottom: { style: "medium", color: { argb: BRIGHT } } };
  });

  model.rows.forEach((rowValues, index) => {
    const r = PLAN_FIRST_ROW + index;
    const [month, initial, interest, principal, extra, total, final] = rowValues;
    const values = [
      month,
      formula(index === 0 ? `${RESUMEN}!${CELL.amount}` : `G${r - 1}`, initial),
      formula(`B${r}*${RESUMEN}!${CELL.rate}/12`, interest),
      formula(`IF(A${r}=${RESUMEN}!${CELL.term},B${r},MIN(B${r},MAX(0,${RESUMEN}!${CELL.payment}-C${r})))`, principal),
      formula(`MIN(${extra},B${r}-D${r})`, extra),
      formula(`C${r}+D${r}+E${r}`, total),
      formula(`MAX(0,B${r}-D${r}-E${r})`, final),
    ];
    const excelRow = sheet.getRow(r);
    excelRow.height = 18;
    const fill = index % 2 === 0 ? WHITE : GRAY;
    values.forEach((value, i) => {
      const cell = excelRow.getCell(i + 1);
      cell.value = value;
      cell.font = { name: FONT, size: 10, color: { argb: i === 5 ? BRIGHT : TEXT }, bold: i === 5 };
      cell.fill = solid(fill);
      cell.border = gridBorder;
      cell.alignment = { vertical: "middle", horizontal: i === 0 ? "center" : "right", indent: i === 0 ? 0 : 1 };
      cell.numFmt = i === 0 ? INTEGER : MONEY;
    });
  });

  const lastDataRow = PLAN_FIRST_ROW + model.rows.length - 1;
  const totalRowIndex = lastDataRow + 1;
  const sum = (col, result) => (model.rows.length
    ? formula(`SUM(${col}${PLAN_FIRST_ROW}:${col}${lastDataRow})`, result)
    : result);
  const totalRow = sheet.getRow(totalRowIndex);
  totalRow.height = 24;
  const totalValues = [
    "TOTAL",
    null,
    sum("C", model.totals.interest),
    sum("D", model.totals.principal),
    sum("E", model.totals.extra),
    sum("F", model.totals.paid),
    null,
  ];
  totalValues.forEach((value, i) => {
    const cell = totalRow.getCell(i + 1);
    cell.value = value;
    cell.font = { name: FONT, bold: true, size: 11, color: { argb: WHITE } };
    cell.fill = solid(BRIGHT);
    cell.border = { top: { style: "medium", color: { argb: NAVY } }, bottom: { style: "medium", color: { argb: NAVY } } };
    cell.alignment = { vertical: "middle", horizontal: i === 0 ? "center" : "right", indent: i === 0 ? 0 : 1 };
    if (i > 1 && i < 6) cell.numFmt = MONEY;
  });

  if (model.rows.length) {
    sheet.autoFilter = { from: { row: PLAN_HEADER_ROW, column: 1 }, to: { row: lastDataRow, column: 7 } };
  }

  sheet.getRow(totalRowIndex + 1).height = 12;
  footerNote(
    sheet,
    totalRowIndex + 2,
    7,
    "Las cifras se recalculan con fórmulas a partir de las condiciones de la hoja Resumen. Las amortizaciones adicionales reducen el saldo directamente y no modifican la cuota base; el pago total del mes incluye interés, capital y amortización extra.",
  );
}

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function exportLoanPlanExcel(input) {
  const model = buildLoanExportModel(input);
  const workbook = await buildLoanWorkbook(model);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBuffer(buffer, `devssoft_prestamo_${stamp()}.xlsx`);
  return model;
}
